import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const FOOD_EXCLUSIONS_KEY = ["food-exclusions"] as const;

/**
 * Type returned by POST /api/v1/food-exclusions.
 */
interface FoodExclusionCreatedResponse {
  exclusions: Array<{ id: string; food_item: string; created_at: string }>;
}

/**
 * React Query hook for managing food exclusions.
 *
 * Provides:
 *  - useQuery for the current list (auto-refreshes on mutation)
 *  - useMutation for adding an item
 *  - useMutation for removing an item
 */
export function useFoodExclusions() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // ── Query: list current exclusions ──────────────────────────────────
  const list = useQuery<string[]>({
    queryKey: FOOD_EXCLUSIONS_KEY,
    queryFn: () => api<string[]>("/api/v1/food-exclusions"),
    staleTime: 30_000,
  });

  // ── Mutation: add an exclusion ──────────────────────────────────────
  const add = useMutation({
    mutationFn: (foodItem: string) =>
      api<FoodExclusionCreatedResponse>("/api/v1/food-exclusions", {
        method: "POST",
        body: { food_item: foodItem },
      }),
    onSuccess: () => {
      toast.success(t("food_exclusions.added"));
      queryClient.invalidateQueries({ queryKey: FOOD_EXCLUSIONS_KEY });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.status === 409) {
        toast.error(t("food_exclusions.duplicate"));
      } else {
        toast.error(t("food_exclusions.error_add"));
      }
    },
  });

  // ── Mutation: remove an exclusion ───────────────────────────────────
  const remove = useMutation({
    mutationFn: (foodItem: string) =>
      api<string[]>(`/api/v1/food-exclusions/${encodeURIComponent(foodItem)}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success(t("food_exclusions.removed"));
      queryClient.invalidateQueries({ queryKey: FOOD_EXCLUSIONS_KEY });
    },
    onError: () => {
      toast.error(t("food_exclusions.error_remove"));
    },
  });

  return {
    /** Current list of excluded food items (string array). */
    exclusions: list.data ?? [],
    /** True while loading the initial list. */
    isLoading: list.isLoading,
    /** True if loading the list failed. */
    isError: list.isError,
    /** Error object from the list query. */
    error: list.error,
    /** Refetch the current list. */
    refetch: list.refetch,

    /** Add a food item to exclusions. Call `.mutate(foodItem)`. */
    add,
    /** Remove a food item from exclusions. Call `.mutate(foodItem)`. */
    remove,
  };
}
